import { useState } from 'react'
import { Palette, Lock, Check } from 'lucide-react'
import {
  THEMES, THEME_ORDER, THEME_PREVIEW, DEFAULT_CUSTOM_VARS,
  getTheme, setTheme as applyThemeChoice, saveCustomTheme, getCustomTheme, canUseTheme, deriveAccentTokens,
} from '../lib/theme'
import { usePlanLimits } from '../lib/account'

/**
 * @typedef {import('../lib/planGate').PlanTier} PlanTier
 * @typedef {keyof typeof THEMES} ThemeKey
 */

/**
 * Indexed lookup into THEMES that accepts any string and returns the exact theme
 * definition or undefined when the key is unknown.
 * @param {string} key
 */
function getThemeDef(key) {
  return /** @type {Record<string, typeof THEMES[ThemeKey] | undefined>} */ (THEMES)[key]
}

/**
 * Preview swatches for a given theme key. Used to render mini-tiles in the picker.
 * @param {string} key
 */
function getPreview(key) {
  return /** @type {Record<string, typeof THEME_PREVIEW[keyof typeof THEME_PREVIEW] | undefined>} */ (THEME_PREVIEW)[key]
}

// Neutral-var inputs in the custom builder. `derives` lists co-vars that are
// auto-computed from the primary pick (additive toward white for positive adj,
// toward black for negative).
/** @type {Array<{ key: string, label: string, derives?: Array<{ key: string, adj: number }> }>} */
const NEUTRAL_INPUTS = [
  { key: '--color-bg',        label: 'Background',     derives: [{ key: '--color-card-alt', adj: 10 }] },
  { key: '--color-card',      label: 'Card Surface' },
  { key: '--color-sidebar',   label: 'Sidebar',        derives: [{ key: '--color-logo-bg', adj: 15 }] },
  { key: '--color-text',      label: 'Text Primary' },
  { key: '--color-text-dim',  label: 'Text Secondary', derives: [{ key: '--color-text-muted', adj: -20 }, { key: '--color-text-faint', adj: -40 }] },
  { key: '--color-border',    label: 'Borders',        derives: [{ key: '--color-border-subtle', adj: -5 }, { key: '--color-border-strong', adj: 10 }] },
]

export default function ThemePicker() {
  const { plan } = usePlanLimits()
  const [current, setCurrent] = useState(getTheme())
  const [showCustom, setShowCustom] = useState(false)
  const [customVars, setCustomVars] = useState(() => {
    const saved = getCustomTheme()
    return saved || { ...DEFAULT_CUSTOM_VARS }
  })

  /** @param {string} key */
  function handleSelect(key) {
    if (!canUseTheme(key, plan)) return
    if (key === 'custom') { setShowCustom(true); return }
    applyThemeChoice(key)
    setCurrent(key)
  }

  function handleSaveCustom() {
    saveCustomTheme(customVars)
    setCurrent('custom')
    setShowCustom(false)
  }

  /**
   * Handle neutral color change — sets primary + any derived vars.
   * @param {string} key
   * @param {string} val  hex
   * @param {Array<{ key: string, adj: number }> | undefined} derives
   */
  function handleNeutralChange(key, val, derives) {
    const r = parseInt(val.slice(1, 3), 16) || 0
    const g = parseInt(val.slice(3, 5), 16) || 0
    const b = parseInt(val.slice(5, 7), 16) || 0
    /** @type {Record<string, string>} */
    const updates = { [key]: val }
    if (derives) {
      derives.forEach(({ key: dk, adj }) => {
        updates[dk] = '#' + [r, g, b].map(c => Math.max(0, Math.min(255, c + adj)).toString(16).padStart(2, '0')).join('')
      })
    }
    setCustomVars(v => ({ ...v, ...updates }))
  }

  /** @param {string} hex */
  function handleAccentChange(hex) {
    const tokens = deriveAccentTokens(hex)
    setCustomVars(v => ({ ...v, ...tokens }))
  }

  /**
   * @param {{ themeKey: string, current: string, plan: PlanTier, onSelect: (key: string) => void }} props
   */
  function ThemeCard({ themeKey, current, plan, onSelect }) {
    const theme = getThemeDef(themeKey)
    if (!theme) return null
    const preview = getPreview(themeKey)
    const isActive = current === themeKey
    const locked = !canUseTheme(themeKey, plan)
    const tierLabel = theme.tier === 'starter' ? 'Starter' : theme.tier === 'pro' ? 'Pro' : null

    return (
      <button onClick={() => !locked && onSelect(themeKey)}
        className={`relative rounded-xl border-2 p-3 text-left transition-all ${isActive ? 'border-accent-500 ring-2 ring-accent-200' : 'border-gray-200 hover:border-gray-300'} ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
        {preview ? (
          <div className="rounded-lg overflow-hidden border border-gray-300 mb-2 h-10 flex">
            <div className="w-6 h-full" style={{ backgroundColor: preview.sidebar, borderRight: `1px solid ${preview.border}` }} />
            <div className="flex-1 p-1.5 flex items-center gap-1.5" style={{ backgroundColor: preview.bg }}>
              <div className="w-10 h-3 rounded-sm" style={{ backgroundColor: preview.card, border: `1px solid ${preview.border}` }} />
              <div className="w-6 h-2 rounded-sm" style={{ backgroundColor: preview.text, opacity: 0.7 }} />
              <div className="w-4 h-2 rounded-sm" style={{ backgroundColor: preview.textDim, opacity: 0.7 }} />
              <div className="ml-auto w-2 h-2 rounded-full" style={{ backgroundColor: preview.accent }} />
            </div>
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden border-2 border-dashed border-gray-300 mb-2 h-10 flex items-center justify-center">
            <Palette size={14} className="text-gray-300" />
          </div>
        )}
        <div className="text-xs font-semibold text-gray-800">{theme.label}</div>
        {locked && tierLabel && (
          <div className="absolute top-2 right-2 flex items-center gap-1">
            <Lock size={10} className="text-gray-400" />
            <span className="text-[9px] text-gray-400 font-medium">{tierLabel}</span>
          </div>
        )}
        {isActive && (
          <div className="absolute top-2 right-2">
            <Check size={14} className="text-accent-600" />
          </div>
        )}
      </button>
    )
  }

  return (
    <div>
      {/* Light themes */}
      <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium mb-1.5">Light</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {THEME_ORDER.filter(k => getThemeDef(k)?.group === 'light').map(key => <ThemeCard key={key} themeKey={key} current={current} plan={plan} onSelect={handleSelect} />)}
      </div>
      {/* Dark themes */}
      <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium mb-1.5">Dark</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {THEME_ORDER.filter(k => getThemeDef(k)?.group === 'dark').map(key => <ThemeCard key={key} themeKey={key} current={current} plan={plan} onSelect={handleSelect} />)}
      </div>
      {/* Custom */}
      <div className="grid grid-cols-3 gap-2">
        <ThemeCard themeKey="custom" current={current} plan={plan} onSelect={handleSelect} />
      </div>

      {/* Custom Theme Builder (Pro) */}
      {showCustom && (
        <div className="mt-4 bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-800">Custom Theme</h4>

          {/* Neutrals */}
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium mb-2">Surfaces & Text</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {NEUTRAL_INPUTS.map(({ key, label, derives }) => (
                <div key={key}>
                  <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">{label}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={customVars[key] || '#000000'}
                      onChange={e => handleNeutralChange(key, e.target.value, derives)}
                      className="w-8 h-8 rounded border-0 cursor-pointer" style={{ padding: 0 }} />
                    <input type="text" value={customVars[key] || ''}
                      onChange={e => setCustomVars(v => ({ ...v, [key]: e.target.value }))}
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Accent */}
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium mb-2">Accent</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">Accent Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={customVars['--color-accent-solid'] || '#5688d1'}
                    onChange={e => handleAccentChange(e.target.value)}
                    className="w-8 h-8 rounded border-0 cursor-pointer" style={{ padding: 0 }} />
                  <input type="text" value={customVars['--color-accent-solid'] || ''}
                    onChange={e => handleAccentChange(e.target.value)}
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Drives nav active, CTAs, links, focus ring</p>
              </div>
            </div>
          </div>

          {/* Live preview strip */}
          <div className="rounded-lg overflow-hidden border border-gray-300">
            <div className="flex h-12" style={{ background: customVars['--color-bg'] }}>
              <div className="w-16 h-full" style={{ background: customVars['--color-sidebar'], borderRight: `1px solid ${customVars['--color-border']}` }} />
              <div className="flex-1 p-2 flex items-center gap-3">
                <span className="text-xs font-semibold" style={{ color: customVars['--color-text'] }}>Preview Text</span>
                <span className="text-[10px]" style={{ color: customVars['--color-text-dim'] }}>Secondary</span>
                <div className="ml-auto w-16 h-6 rounded flex items-center justify-center text-[10px] font-medium"
                  style={{ background: customVars['--color-accent-solid'], color: '#fff' }}>CTA</div>
                <div className="w-16 h-6 rounded" style={{ background: customVars['--color-card'], border: `1px solid ${customVars['--color-border']}` }} />
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCustom(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
            <button onClick={handleSaveCustom} className="px-3 py-1.5 text-xs font-medium text-white bg-accent-600 rounded hover:bg-accent-700">Apply Custom Theme</button>
          </div>
        </div>
      )}
    </div>
  )
}
